import { useRef, useState } from "react";
import { ApolloError } from "@apollo/client";
import { isEqual } from "lodash";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useCourieStore } from "@src/common/lib/store";

interface UseGuardedSubmissionProps {
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

type Continuation = () => Promise<void>;

interface PendingContinuation {
  continuation: Continuation;
  submittedData: unknown;
}

/**
 * Runs one submission at a time. The step after the mutation is remembered, so a failed
 * follow-up (like a refetch) is retried without sending the mutation again. The retry only
 * runs for the same form data; a form edited since the save is refused, not saved partly.
 */
export function useGuardedSubmission({ onSubmittingChange }: UseGuardedSubmissionProps) {
  const showError = useErrorToast();
  const { showToast } = useCourieStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isRunningRef = useRef(false);
  const pendingContinuationRef = useRef<PendingContinuation | null>(null);

  const runSubmission = (operation: () => Promise<unknown>) => {
    isRunningRef.current = true;
    setIsSubmitting(true);
    onSubmittingChange?.(true);
    return operation()
      .then((result) => {
        pendingContinuationRef.current = null;
        return result;
      })
      .catch((err) => {
        showError(err as ApolloError);
      })
      .finally(() => {
        isRunningRef.current = false;
        setIsSubmitting(false);
        onSubmittingChange?.(false);
      });
  };

  return {
    isSubmitting,
    isRunning: () => isRunningRef.current,
    runSubmission,
    rememberContinuation: (continuation: Continuation, submittedData: unknown) => {
      pendingContinuationRef.current = { continuation, submittedData };
    },
    retryContinuation: (currentData: unknown): Promise<unknown> | undefined => {
      const pending: PendingContinuation | null = pendingContinuationRef.current;
      if (!pending) return undefined;
      if (!isEqual(currentData, pending.submittedData)) {
        showToast({
          type: "error",
          message: "Order saved, but not your latest edits. Reopen it to make them.",
        });
        return Promise.resolve();
      }
      return runSubmission(pending.continuation);
    },
  };
}
